<template>
  <TransitionGroup :name="transitionName">
    <FormKit
      v-slot="{ value, state: { valid } }"
      v-for="(step, i) in steps"
      v-show="step.code === stepCode"
      :key="step.code"
      :id="`questionnaireForm-${step.code}`"
      v-model="model"
      type="form"
      :plugins="[stepPlugin]"
      :actions="false"
      @submit="submit"
    >
      <QuestionnaireStep :step="step">
        <LSButton
          v-show="i > 0"
          variant="outline"
          class="mr-8 mb-8"
          wide
          @click.prevent="
            event => {
              // trigger back on click or touch even only. DOes not allow triggering by enter pressed
              if (['touch', 'pen', 'mouse'].includes(event.pointerType)) {
                backOrPrevious(value, step.code)
                return false
              }
            }
          "
          >Zurück
        </LSButton>
        <LSButton
          id="next-button"
          v-show="stepValid"
          :type="isLastStepCalc(value, step.code) ? 'primary' : 'secondary'"
          class="mb-8"
          wide
          @click.prevent="
            data => {
              nextOrSubmit(data, `questionnaireForm-${step.code}`, isLastStepCalc(value, step.code))
            }
          "
          >{{ isLastStepCalc(value, step.code) ? 'Absenden' : 'Weiter' }}
        </LSButton>
        <p v-if="!valid" class="text-slate-600 font-light text-base leading-6">Scrolle hoch, um deine Eingaben zu überprüfen.</p>
      </QuestionnaireStep>
    </FormKit>
  </TransitionGroup>

  <!-- FIXME: completed via form validation -->
</template>

<script setup lang="ts">
import QuestionnaireStep from '@/questionnaire/components/QuestionnaireStep.vue'
import LSButton from '@/components/LSButton.vue'
import useSteps from '@/utils/useSteps'

import { computed, nextTick, ref, watch } from 'vue'
import { onBeforeRouteUpdate, useRouter } from 'vue-router'
import { useQuestionnaireStore } from '../stores/questionnaire'
import { submitForm } from '@formkit/vue'
import type { TemplateVariableValue } from '../types'
import { updateDocument } from '@/services/documentsService'
import { errorNotification } from '@/utils/notifications'
import { getTemplateVariablesArray } from '@/utils/apiFormatters'
import type { FormKitGroupValue } from '@formkit/core'

// some type aliases mainly to document the structure of the form
type SectionName = string
type template_variable_code = string
type Answers = Record<template_variable_code, TemplateVariableValue>
type FormData = Record<SectionName, Answers>

const props = defineProps<{ documentId: string; stepCode: string }>()
const store = useQuestionnaireStore()
const { steps } = store
const { stepPlugin, stepValidity } = useSteps()
const router = useRouter()
const { documentId } = props
const stepValid = computed(() => stepValidity[props.stepCode]?.valid ?? true)
const model = ref({})

const getNextOrPreviousStepCode = (formData: FormKitGroupValue | undefined, currentStep: string, forwardStep = true) => {
  const tempsSteps = steps.map(({ code }) => code).filter(tempStep => tempStep && formData && Object.keys(formData[tempStep] || {})?.length)
  const nextIndex = tempsSteps.findIndex(code => code === currentStep) + (forwardStep ? 1 : -1)
  return tempsSteps[nextIndex]
}

const backOrPrevious = (formData: FormKitGroupValue | undefined, currentStep: string) => {
  const previousStepPath = `/questionnaire/${documentId}/${getNextOrPreviousStepCode(formData, currentStep, false)}/`
  if (previousStepPath === router.options.history.state.back) {
    router.back()
  } else {
    router.push(previousStepPath)
  }
}

watch(stepValid, async newValue => {
  await nextTick(() => {
    const el = document.getElementById('next-button')
    if (newValue && el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  })
})

const transitionName = computed(() => {
  const stepIndex = (stepCode: string) => {
    return steps.findIndex(step => step.code === stepCode)
  }

  const isPreviousStep = stepIndex(props.stepCode) < stepIndex(fromStepCode.value)
  return `step${isPreviousStep ? '-reverse' : ''}`
})

const isLastStep = ref(false)

const isLastStepCalc = (formData: FormKitGroupValue | undefined, currentStep: string) => {
  const tempsSteps = steps.map(({ code }) => code).filter(tempStep => tempStep && formData && Object.keys(formData[tempStep] || {})?.length)
  return tempsSteps[tempsSteps.length - 1] === currentStep
}

const submit = (data: FormData) => {
  let newTemplateVariableValues: Record<string, TemplateVariableValue> = {}

  if (props.stepCode && !!data[props.stepCode]) {
    newTemplateVariableValues = { ...newTemplateVariableValues, ...data[props.stepCode] }
  } else {
    for (const k in data) {
      newTemplateVariableValues = { ...newTemplateVariableValues, ...data[k] }
    }
  }

  const preparedEntries = getTemplateVariablesArray(newTemplateVariableValues)

  const executeNavigation = () => {
    if (isLastStep.value) {
      router.push({ name: 'legalDocument', params: { documentId } })
    } else {
      router.push({
        name: 'questionnaire',
        params: { documentId, stepCode: getNextOrPreviousStepCode(data, props.stepCode) },
      })
    }
  }

  if (!preparedEntries.length) {
    executeNavigation()
    return
  }
  updateDocument(documentId, preparedEntries, {})
    .then(() => {
      store.$patch({ ...store.templateVariableValues, ...newTemplateVariableValues })
      executeNavigation()
    })
    .catch(error => {
      errorNotification(error?.response?.data?.error)
    })
}

const nextOrSubmit = (event: Event, formId: string, lastStep = false) => {
  if (!stepValid.value) {
    return
  }

  isLastStep.value = lastStep
  submitForm(formId)
}

const fromStepCode = ref(props.stepCode)

onBeforeRouteUpdate((to, from, next) => {
  if (to.name === 'questionnaire') {
    fromStepCode.value =
      from.name === 'questionnaire' && from.params.stepCode ? from.params.stepCode.toString() : steps[steps.length - 1]?.code
  }
  next()
})
</script>

<style>
:root {
  --step-transition-to: 0px;
}

.slide-reverse-enter-active,
.slide-reverse-leave-active {
  transition: 500ms;
}

.slide-enter-active,
.slide-leave-active {
  transition: 1s;
}

.slide-reverse-enter-from {
  transform: translateY(-200%);
}

.slide-reverse-enter-to {
  transform: translateY(0);
}

.slide-reverse-leave-from {
  transform: translateY(var(--step-transition-to));
}

.slide-reverse-leave-to {
  transform: translateY(100vh);
}

.slide-enter-from {
  transform: translateY(100vh);
}

.slide-enter-to {
  transform: translateY(var(--step-transition-to));
}

.slide-leave-to {
  transform: translateY(-100vh);
}

.formkit-outer[data-disabled] {
  opacity: 1 !important;
}
.formkit-outer[data-disabled] .formkit-inner {
  opacity: 0.5 !important;
}
</style>
